import React, { useEffect, useRef } from "react";
import { AlertCircle, LoaderCircle, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { AUTH_STATUSES, useAuth } from "../contexts/AuthContext";

export const AuthCard = ({
  icon: Icon = ShieldCheck,
  iconClassName = "",
  eyebrow,
  title,
  description,
  children,
  className = "max-w-sm",
}) => (
  <div
    className={`w-full ${className} rounded-[28px] border border-zinc-800 bg-zinc-900/90 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.42)] backdrop-blur-xl sm:p-7`}
  >
    <div className="mb-6 flex items-start gap-4">
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-zinc-800 text-zinc-200">
        <Icon className={`h-5 w-5 ${iconClassName}`.trim()} />
      </div>
      <div className="min-w-0">
        {eyebrow ? (
          <p className="mb-2 text-[11px] uppercase tracking-[0.24em] text-zinc-500 font-mono">{eyebrow}</p>
        ) : null}
        <h1 className="text-2xl font-light text-zinc-50 font-['Outfit']">{title}</h1>
        {description ? <p className="mt-2 text-sm leading-relaxed text-zinc-400 font-['Manrope']">{description}</p> : null}
      </div>
    </div>
    {children}
  </div>
);

export const AuthNotice = ({ children, tone = "neutral" }) => {
  const toneClassName =
    tone === "danger"
      ? "border-red-500/30 bg-red-500/10 text-red-100"
      : tone === "success"
        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-100"
        : "border-white/10 bg-black/20 text-zinc-300";

  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm leading-relaxed font-['Manrope'] ${toneClassName}`}>{children}</div>
  );
};

export const AuthFullscreenState = ({
  icon = LoaderCircle,
  iconClassName = "animate-spin",
  title,
  description,
  children,
}) => (
  <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-950 px-6 py-10 text-zinc-50">
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.14),transparent_26%),radial-gradient(circle_at_18%_18%,rgba(255,255,255,0.07),transparent_18%),linear-gradient(135deg,rgba(255,255,255,0.03),transparent_55%)]" />
    <div className="pointer-events-none absolute inset-0 opacity-50 [background-image:radial-gradient(circle_at_center,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:18px_18px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />
    <div className="relative z-10 flex w-full justify-center">
      <AuthCard icon={icon} iconClassName={iconClassName} eyebrow="Secure access" title={title} description={description}>
        {children}
      </AuthCard>
    </div>
  </div>
);

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) {
      return;
    }

    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        navigate("/login", {
          replace: true,
          state: { authError: "That invite link is missing a session token." },
        });
        return;
      }

      try {
        await exchangeSession(sessionIdMatch[1]);
        navigate("/dashboard", { replace: true });
      } catch (requestError) {
        const detail =
          typeof requestError?.response?.data?.detail === "string" ? requestError.response.data.detail : undefined;
        navigate("/login", {
          replace: true,
          state: { authError: detail },
        });
      }
    };

    void processSession();
  }, [exchangeSession, location.hash, navigate]);

  return (
    <AuthFullscreenState
      title="Authenticating access"
      description="Validating your secure invite and opening the workspace."
    />
  );
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading, error, authStatus, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || user || location.state?.user) {
      return;
    }

    if (authStatus === AUTH_STATUSES.UNAUTHENTICATED) {
      navigate("/login", {
        replace: true,
        state: {
          from: `${location.pathname}${location.search}${location.hash}`,
        },
      });
    }
  }, [authStatus, loading, location.hash, location.pathname, location.search, location.state, navigate, user]);

  if (loading) {
    return (
      <AuthFullscreenState
        title="Checking access"
        description="Verifying your secure session before we open the workspace."
      />
    );
  }

  if (authStatus === AUTH_STATUSES.UNAVAILABLE) {
    return (
      <AuthFullscreenState
        icon={AlertCircle}
        iconClassName=""
        title="Authentication unavailable"
        description={error || "We couldn't reach the authentication service right now."}
      >
        <div className="space-y-4">
          <AuthNotice tone="danger">
            Protected routes stay closed until the session service responds. This is deliberate so the app never feels
            like it silently broke.
          </AuthNotice>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                void checkAuth();
              }}
              className="flex-1 rounded-xl bg-white px-4 py-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
            >
              Retry access check
            </button>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              className="flex-1 rounded-xl border border-zinc-700 px-4 py-3 text-sm font-medium text-zinc-200 transition-colors hover:border-zinc-500 hover:bg-zinc-800"
            >
              Back to home
            </button>
          </div>
        </div>
      </AuthFullscreenState>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};
